import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  usePaymentConfigCurrent,
  usePaymentConfigHistory,
  useUpdatePaymentConfig,
} from '@/hooks/commercial/use-payment-config';
import {
  type PaymentConfigFormData,
  paymentConfigSchema,
} from '@/schemas/commercial/payment-config.schema';

interface PaymentConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FIELD_LABELS: Record<string, string> = {
  minEntradaPercent: 'Porcentagem mínima de entrada',
  maxInstallments: 'Quantidade máxima de parcelas',
};

function formatAuditValue(field: string, value: string | number): string {
  if (field === 'minEntradaPercent') return `${value}%`;
  if (field === 'maxInstallments') return `${value}x`;
  return String(value);
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PaymentConfigModal({ open, onOpenChange }: PaymentConfigModalProps) {
  const [historyPage, setHistoryPage] = useState(1);

  const { data: currentConfig, isLoading: isLoadingConfig } = usePaymentConfigCurrent();
  const { data: historyData } = usePaymentConfigHistory(historyPage);
  const updateMutation = useUpdatePaymentConfig();

  const form = useForm<PaymentConfigFormData>({
    resolver: zodResolver(paymentConfigSchema),
    defaultValues: {
      downPaymentPercentage: undefined,
      installments: undefined,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: hydrate form when config loads
  useEffect(() => {
    if (currentConfig) {
      form.reset({
        downPaymentPercentage: currentConfig.minEntradaPercent,
        installments: currentConfig.maxInstallments,
      });
    }
  }, [currentConfig]);

  const handleSubmit = form.handleSubmit((data) => {
    updateMutation.mutate(data, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  });

  const historyDocs = historyData?.docs ?? [];
  const totalItems = historyData?.total ?? 0;
  const limit = historyData?.limit ?? 10;
  const startItem = totalItems > 0 ? (historyPage - 1) * limit + 1 : 0;
  const endItem = totalItems > 0 ? Math.min(historyPage * limit, totalItems) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] min-h-[60vh] w-[calc(100vw-2rem)] max-w-[1000px] overflow-x-hidden p-6 sm:max-w-[1000px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Settings className="h-5 w-5" />
            Configurações de pagamento
          </DialogTitle>
        </DialogHeader>

        {isLoadingConfig ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 flex min-w-0 flex-col min-h-0 overflow-y-auto overflow-x-hidden pr-1">
            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div className="grid min-w-0 grid-cols-1 items-end gap-4 sm:grid-cols-[minmax(0,180px)_minmax(0,180px)_max-content]">
                <div className="min-w-0 space-y-1.5">
                  <label
                    htmlFor="downPaymentPercentage"
                    className="text-sm font-medium text-gray-700"
                  >
                    Porcentagem de entrada*
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="Ex: 30"
                    id="downPaymentPercentage"
                    className="h-10 w-full min-w-0"
                    {...form.register('downPaymentPercentage', {
                      valueAsNumber: true,
                    })}
                  />
                  {form.formState.errors.downPaymentPercentage && (
                    <p className="text-xs text-red-500">
                      {form.formState.errors.downPaymentPercentage.message}
                    </p>
                  )}
                </div>
                <div className="min-w-0 space-y-1.5">
                  <label htmlFor="installments" className="text-sm font-medium text-gray-700">
                    Quantidade de parcelas*
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={12}
                    placeholder="Ex: 6"
                    id="installments"
                    className="h-10 w-full min-w-0"
                    {...form.register('installments', { valueAsNumber: true })}
                  />
                  {form.formState.errors.installments && (
                    <p className="text-xs text-red-500">
                      {form.formState.errors.installments.message}
                    </p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="h-10 shrink-0 bg-black px-6 text-sm font-medium text-white hover:bg-gray-800"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                  {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>

            {/* Divider after form */}
            <div className="border-t border-gray-200 mt-6" />

            {/* Histórico */}
            <div className="flex-1 flex min-w-0 flex-col mt-5 min-h-0">
              <p className="text-base font-bold text-gray-900 mb-3">Histórico</p>

              <div className="w-full min-w-0 flex-1 overflow-x-auto rounded-md border flex flex-col">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px] text-xs font-bold text-gray-500 uppercase">
                        DATA/HORA
                      </TableHead>
                      <TableHead className="w-[200px] text-xs font-bold text-gray-500 uppercase">
                        USUÁRIO
                      </TableHead>
                      <TableHead className="text-xs font-bold text-gray-500 uppercase">
                        AÇÃO
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyDocs.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="h-24 text-center text-sm text-muted-foreground"
                        >
                          Nenhum histórico encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      historyDocs.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="whitespace-nowrap text-xs">
                            {formatDateTime(entry.createdAt)}
                          </TableCell>
                          <TableCell className="text-xs">{entry.userName || '-'}</TableCell>
                          <TableCell className="text-xs">
                            {entry.changes.map((change) => (
                              <div key={`${change.field}-${change.from}-${change.to}`}>
                                <p>{FIELD_LABELS[change.field] ?? change.field}</p>
                                <p className="text-muted-foreground">
                                  De: {formatAuditValue(change.field, change.from)} para:{' '}
                                  {formatAuditValue(change.field, change.to)}
                                </p>
                              </div>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Divider before pagination */}
            <div className="border-t border-gray-200 mt-4" />

            {/* Pagination footer */}
            <div className="flex flex-wrap items-center justify-between gap-2 py-3">
              <p className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{startItem}</span> a{' '}
                <span className="font-medium">{endItem}</span> de{' '}
                <span className="font-medium">{totalItems}</span> resultados
              </p>
              <div className="flex flex-wrap items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryPage(historyPage - 1)}
                  disabled={historyPage <= 1}
                >
                  <span className="text-xs">Anterior</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-black text-white hover:bg-gray-800 hover:text-white pointer-events-none"
                >
                  {historyPage}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setHistoryPage(historyPage + 1)}
                  disabled={historyPage >= (historyData?.pages ?? 1)}
                >
                  <span className="text-xs">Próximo</span>
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
